import { useState, useCallback, useMemo } from 'react';
import { Status } from 'hooks/requests/fetchUtils';
import useFetchApi from 'hooks/useFetchApi';
import useIovation from 'hooks/useIovation';
import useQueryParameters from 'hooks/useQueryParameters';
import { selectAccounts, selectInstantCredit } from 'state/instantCredit';
import { useSelector } from 'react-redux';
import { indexOf } from 'ramda';

const PATH = '/api/text_to_recover';

type TextToRecoverHook = () => [() => Promise<void>, Status, boolean];

export class TextToRecoverError extends Error {}

export const useTextToRecover: TextToRecoverHook = () => {
  const [error, setError] = useState<boolean>(false);
  const [status, fetch] = useFetchApi();

  const iovationBlackbox = useIovation();

  const {
    cardType: qpCardType,
    overrideKey,
    mockTTR: mockResponse,
    location,
    channel,
    subchannel,
    applicationSubType,
  } = useQueryParameters();

  const { logo, divisionNumber, cardType: icCardType } = useSelector(
    selectInstantCredit,
  );

  const accounts = useSelector(selectAccounts);
  const currentAccount = useSelector(selectInstantCredit);

  const accountIndex = useMemo(() => {
    const index = indexOf(currentAccount, accounts);
    return index !== -1 ? index : undefined;
  }, [currentAccount, accounts]);

  const sendTextToRecover = useCallback(async (): Promise<void> => {
    setError(false);

    const blackbox = await iovationBlackbox;
    const response = await fetch(PATH, {
      method: 'POST',
      body: {
        iovationBlackbox: blackbox,
        cardType: icCardType || qpCardType,
        overrideKey,
        logo: logo,
        location,
        channel,
        subchannel,
        applicationSubType,
        mockResponse,
        divisionNumber: divisionNumber,
        accountIndex,
      },
    });

    if (!response.ok) {
      setError(true);
      return Promise.reject(new TextToRecoverError());
    }
  }, [
    iovationBlackbox,
    fetch,
    icCardType,
    qpCardType,
    overrideKey,
    logo,
    location,
    channel,
    subchannel,
    applicationSubType,
    mockResponse,
    divisionNumber,
    accountIndex,
  ]);

  return [sendTextToRecover, status, error];
};

export default useTextToRecover;
